import { CHANNELS, DELIVERY_DAY_CHOICES, emptySuggestKeys, FORMATS, INCLUDES, MAX_DELIVERY_DAYS, SPEC_LABELS, SUBTITLE_LANGS, specLabel, type Advertising, type DeliverySpec, type SpecKey, type SuggestSpecKey } from '@kff/domain';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { Icon } from '../shell.js';
import { MoreOptions } from '../ui.js';
import { SpecSuggestDialog, type Suggestion } from './specSuggest.js';

/**
 * "Leverans" on a project's Details (owner 2026-11-01, thread "Matcha Mondays projektdetaljer i Hubben"): what Monday kept
 * per project – formats, channels, subtitles, organic or paid, delivery time, number and length, what the film contains,
 * the billing company and the client's reference; the planning meeting and "runs until" under Fler val. No Save button:
 * a chip saves on click, text when you leave the field, and "Saved ✓" shows by the field. Monday projects and people who
 * do not run the project see it read only, empty rows left out.
 */

export interface DeliveryInfo {
  days: number;
  source: 'project' | 'agreement' | 'standard';
  agreementDays: number | null;
  agreementRef: string | null;
  express: boolean;
  expressPrice: number | null;
}
export interface BillTo {
  current: string | null;
  main: string;
  companies: { id: string; name: string; orgNo: string | null }[];
  canAdd?: boolean;
}
interface ProjectLike {
  id: string;
  client: { id: string; name: string };
  spec?: DeliverySpec;
  delivery?: DeliveryInfo | null;
  billTo?: BillTo | null;
  mondayOwned?: boolean;
  can: { edit: boolean };
}

const TX = texts(
  {
    title: 'Delivery',
    lengths: 'Number and length',
    lengthsHint: 'e.g. 1 film of 90 s + 6 short clips',
    formats: 'Formats',
    channels: 'Channels',
    subtitles: 'Subtitles',
    no: 'No',
    advertising: 'Advertising',
    deliveryDays: 'Delivery time',
    includes: 'Includes',
    billTo: 'Billing company',
    mainCompany: (n: string) => `${n} (standard)`,
    addCompany: '+ Add company',
    companyName: 'Company name',
    orgNo: 'Org no',
    add: 'Add',
    cancel: 'Cancel',
    reference: 'Project reference',
    referenceHint: 'PO or the client’s reference',
    planningDate: 'Planning meeting',
    untilMonth: 'Runs until',
    untilHint: 'a project over several months',
    other: '+ other',
    otherLang: '+ language',
    otherDays: 'Other',
    days: (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`,
    perAgreement: (n: number) => `As agreed (${n} d)`,
    standard: (n: number) => `Standard (${n} d)`,
    fromAgreement: (n: number, ref: string | null) => `${n} working days as in the agreement${ref ? ` (${ref})` : ''}.`,
    fromStandard: (n: number) => `No delivery time in the agreement: ${n} working days.`,
    rule: 'The first draft counts from the shoot day; the next drafts 3 working days after feedback.',
    express: (price: number | null) => `Shorter than the agreement: express${price ? `, add-on ${price.toLocaleString('sv-SE')} kr as agreed` : ''}.`,
    saved: 'Saved',
    saving: 'Saving…',
    failed: 'Could not save, try again',
    inMonday: 'Changed in Monday. The Hub reads it every 15 minutes.',
    empty: 'Nothing filled in yet.',
    suggest: 'Suggest from meetings',
    suggestHint: 'Klara reads the client’s meetings and suggests the empty fields'
  },
  {
    title: 'Leverans',
    lengths: 'Antal och längd',
    lengthsHint: 't.ex. 1 film à 90 s + 6 korta klipp',
    formats: 'Format',
    channels: 'Kanaler',
    subtitles: 'Undertexter',
    no: 'Nej',
    advertising: 'Annonsering',
    deliveryDays: 'Leveranstid',
    includes: 'Innehåller',
    billTo: 'Fakturabolag',
    mainCompany: (n: string) => `${n} (standard)`,
    addCompany: '+ Lägg till bolag',
    companyName: 'Bolagets namn',
    orgNo: 'Org.nr',
    add: 'Lägg till',
    cancel: 'Avbryt',
    reference: 'Projektreferens',
    referenceHint: 'PO eller kundens referens',
    planningDate: 'Planeringsmöte',
    untilMonth: 'Pågår till',
    untilHint: 'projekt över flera månader',
    other: '+ annat',
    otherLang: '+ språk',
    otherDays: 'Annat',
    days: (n: number) => `${n} ${n === 1 ? 'dag' : 'dagar'}`,
    perAgreement: (n: number) => `Enligt avtal (${n} d)`,
    standard: (n: number) => `Standard (${n} d)`,
    fromAgreement: (n: number, ref: string | null) => `${n} arbetsdagar enligt avtalet${ref ? ` (${ref})` : ''}.`,
    fromStandard: (n: number) => `Avtalet anger ingen leveranstid: ${n} arbetsdagar.`,
    rule: 'Första utkastet räknas från inspelningsdagen, nästa utkast 3 arbetsdagar efter feedback.',
    express: (price: number | null) => `Kortare än avtalet: express${price ? `, tillägg ${price.toLocaleString('sv-SE')} kr enligt avtal` : ''}.`,
    saved: 'Sparat',
    saving: 'Sparar…',
    failed: 'Kunde inte spara, försök igen',
    inMonday: 'Ändras i Monday. Hubben hämtar det var 15:e minut.',
    empty: 'Inget ifyllt än.',
    suggest: 'Förslag från möten',
    suggestHint: 'Klara läser kundens möten och föreslår de tomma fälten'
  }
);

type State = 'saving' | 'saved' | { error: string };
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function DeliveryBox<V extends ProjectLike>(p: { api: Api; v: V; onSaved: (v: V) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const { v } = p;
  const edit = v.can.edit && !v.mondayOwned;
  const s = v.spec;
  const [state, setState] = useState<Partial<Record<SpecKey | 'billTo', State>>>({});
  const [suggesting, setSuggesting] = useState(false);
  const timers = useRef<Record<string, number>>({});
  useEffect(() => () => Object.values(timers.current).forEach((x) => clearTimeout(x)), []);
  if (!s) return null;

  const mark = (k: SpecKey | 'billTo', st: State | null) => {
    clearTimeout(timers.current[k]);
    setState((o) => {
      const n = { ...o };
      if (st) n[k] = st;
      else delete n[k];
      return n;
    });
    if (st === 'saved') timers.current[k] = setTimeout(() => mark(k, null), 2000) as unknown as number;
  };
  const save = async <K extends SpecKey>(k: K, value: DeliverySpec[K]) => {
    mark(k, 'saving');
    try {
      const r = await p.api.put<{ project: V }>(`/api/projects/${v.id}/spec`, { [k]: value });
      p.onSaved(r.project);
      mark(k, 'saved');
    } catch (e) {
      mark(k, { error: errText(e) });
    }
  };
  const toggle = <K extends 'formats' | 'channels' | 'includes'>(k: K, key: string) => {
    const cur = s[k];
    void save(k, cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]);
  };

  // Klara's picks from the meetings, saved together; each field shows "Saved ✓" as if clicked
  const applySuggested = async (patch: Partial<DeliverySpec>) => {
    const keys = Object.keys(patch) as SpecKey[];
    if (!keys.length) return;
    const r = await p.api.put<{ project: V }>(`/api/projects/${v.id}/spec`, patch);
    p.onSaved(r.project);
    keys.forEach((k) => mark(k, 'saved'));
  };

  const ro = !edit;
  const has = (k: SpecKey) => {
    const x = s[k];
    return Array.isArray(x) ? x.length > 0 || (k === 'subtitles' && x !== null) : x !== null;
  };
  const list = (k: 'formats' | 'channels' | 'includes' | 'subtitles') =>
    k === 'subtitles' && s.subtitles && !s.subtitles.length ? t.no : (s[k] ?? []).map((x) => (k === 'formats' ? x : specLabel(k, x, lang))).join(', ');

  // read only: only what is filled in
  if (ro) {
    const rows: [string, string][] = [];
    if (has('lengths')) rows.push([t.lengths, s.lengths!]);
    if (has('formats')) rows.push([t.formats, list('formats')]);
    if (has('channels')) rows.push([t.channels, list('channels')]);
    if (has('subtitles')) rows.push([t.subtitles, list('subtitles')]);
    if (has('advertising')) rows.push([t.advertising, SPEC_LABELS.advertising[s.advertising!][lang]]);
    if (v.delivery) rows.push([t.deliveryDays, t.days(v.delivery.days)]);
    if (has('includes')) rows.push([t.includes, list('includes')]);
    if (has('reference')) rows.push([t.reference, s.reference!]);
    if (has('planningDate')) rows.push([t.planningDate, s.planningDate!]);
    if (has('untilMonth')) rows.push([t.untilMonth, s.untilMonth!]);
    if (!rows.length && !v.mondayOwned) return null;
    return (
      <section class="blk pd-dlv" aria-labelledby="pdl-h">
        <h2 class="blk-title" id="pdl-h">
          {t.title}
        </h2>
        {rows.length ? (
          <dl class="props">
            {rows.map(([k, x]) => (
              <div class="prop">
                <dt>{k}</dt>
                <dd>{x}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p class="muted">{t.empty}</p>
        )}
        {v.mondayOwned ? (
          <p class="tl-note">
            <Icon name="lock" />
            {t.inMonday}
          </p>
        ) : null}
      </section>
    );
  }

  const chip = (on: boolean, label: string, click: () => void) => (
    <button type="button" class="chip" aria-pressed={on} onClick={click}>
      {label}
    </button>
  );

  const d = v.delivery;
  const dayChoices = DELIVERY_DAY_CHOICES as readonly number[];
  const own = s.deliveryDays;
  const custom = own !== null && !dayChoices.includes(own);
  const extra = (k: 'formats' | 'channels' | 'subtitles', fixed: readonly string[]) => (s[k] ?? []).filter((x) => !fixed.includes(x));

  const open = emptySuggestKeys(s);
  const label = (k: SuggestSpecKey) => t[k === 'untilMonth' ? 'untilMonth' : k];
  const show = (x: Suggestion): string => {
    const val = x.value;
    if (x.key === 'subtitles') return Array.isArray(val) && !val.length ? t.no : (val as string[]).map((l) => specLabel('subtitles', l, lang)).join(', ');
    if (x.key === 'channels' || x.key === 'includes') return (val as string[]).map((c) => specLabel(x.key as 'channels' | 'includes', c, lang)).join(', ');
    if (x.key === 'formats') return (val as string[]).join(', ');
    if (x.key === 'advertising') return SPEC_LABELS.advertising[val as Advertising][lang];
    if (x.key === 'deliveryDays') return t.days(val as number);
    return String(val);
  };

  return (
    <section class="blk pd-dlv" aria-labelledby="pdl-h">
      <div class="blk-head">
        <h2 class="blk-title" id="pdl-h">
          {t.title}
        </h2>
        {open.length ? (
          <button type="button" class="btn btn-sm" title={t.suggestHint} onClick={() => setSuggesting(true)}>
            <Icon name="bulb" />
            {t.suggest}
          </button>
        ) : null}
      </div>
      {suggesting ? <SpecSuggestDialog api={p.api} projectId={v.id} label={label} show={show} onApply={applySuggested} onClose={() => setSuggesting(false)} /> : null}
      <dl class="props">
        <Row st={state.lengths} t={t} label={t.lengths} for="dlv-len">
          <TextField id="dlv-len" value={s.lengths} placeholder={t.lengthsHint} max={300} onSave={(x) => save('lengths', x)} />
        </Row>
        <Row st={state.formats} t={t} label={t.formats}>
          <div class="chips dlv-chips">
            {FORMATS.map((f) => chip(s.formats.includes(f), f, () => toggle('formats', f)))}
            {extra('formats', FORMATS).map((f) => chip(true, f, () => toggle('formats', f)))}
            <AddOther label={t.other} onAdd={(x) => save('formats', [...s.formats, x])} />
          </div>
        </Row>
        <Row st={state.channels} t={t} label={t.channels}>
          <div class="chips dlv-chips">
            {CHANNELS.map((c) => chip(s.channels.includes(c), specLabel('channels', c, lang), () => toggle('channels', c)))}
            {extra('channels', CHANNELS).map((c) => chip(true, c, () => toggle('channels', c)))}
            <AddOther label={t.other} onAdd={(x) => save('channels', [...s.channels, x])} />
          </div>
        </Row>
        <Row st={state.subtitles} t={t} label={t.subtitles}>
          <div class="chips dlv-chips">
            {chip(s.subtitles !== null && s.subtitles.length === 0, t.no, () => void save('subtitles', s.subtitles !== null && s.subtitles.length === 0 ? null : []))}
            {SUBTITLE_LANGS.slice(0, 3).map((l) => chip(Boolean(s.subtitles?.includes(l)), specLabel('subtitles', l, lang), () => void save('subtitles', langToggle(s.subtitles, l))))}
            {[...SUBTITLE_LANGS.slice(3).filter((l) => s.subtitles?.includes(l)), ...extra('subtitles', SUBTITLE_LANGS)].map((l) => chip(true, specLabel('subtitles', l, lang), () => void save('subtitles', langToggle(s.subtitles, l))))}
            <AddOther label={t.otherLang} options={SUBTITLE_LANGS.slice(3).filter((l) => !s.subtitles?.includes(l)).map((l) => [l, specLabel('subtitles', l, lang)])} onAdd={(x) => save('subtitles', langToggle(s.subtitles, x))} />
          </div>
        </Row>
        <Row st={state.advertising} t={t} label={t.advertising}>
          <div class="chips dlv-chips">
            {(Object.keys(SPEC_LABELS.advertising) as Advertising[]).map((a) => chip(s.advertising === a, SPEC_LABELS.advertising[a][lang], () => void save('advertising', s.advertising === a ? null : a)))}
          </div>
        </Row>
        <Row st={state.deliveryDays} t={t} label={t.deliveryDays}>
          <div class="chips dlv-chips">
            {d ? chip(own === null, d.agreementDays ? t.perAgreement(d.agreementDays) : t.standard(d.source === 'project' ? 5 : d.days), () => void save('deliveryDays', null)) : null}
            {dayChoices.map((n) => chip(own === n, t.days(n), () => void save('deliveryDays', own === n ? null : n)))}
            <DaysField label={t.otherDays} value={custom ? own : null} onSave={(n) => save('deliveryDays', n)} />
          </div>
          {d ? (
            <p class="small muted dlv-note">
              {d.express ? <span class="warn">{t.express(d.expressPrice)} </span> : own === null ? (d.agreementDays ? t.fromAgreement(d.agreementDays, d.agreementRef) : t.fromStandard(d.days)) + ' ' : null}
              {t.rule}
            </p>
          ) : null}
        </Row>
        <Row st={state.includes} t={t} label={t.includes}>
          <div class="chips dlv-chips">{INCLUDES.map((i) => chip(s.includes.includes(i), specLabel('includes', i, lang), () => toggle('includes', i)))}</div>
        </Row>
        {v.billTo ? (
          <Row st={state.billTo} t={t} label={t.billTo} for="dlv-billto">
            <BillToField api={p.api} v={v} lang={lang} onState={(st) => mark('billTo', st)} onSaved={p.onSaved} />
          </Row>
        ) : null}
        <Row st={state.reference} t={t} label={t.reference} for="dlv-ref">
          <TextField id="dlv-ref" value={s.reference} placeholder={t.referenceHint} max={100} onSave={(x) => save('reference', x)} />
        </Row>
      </dl>
      <MoreOptions id="dlv-more" filled={(s.planningDate ? 1 : 0) + (s.untilMonth ? 1 : 0)}>
        <dl class="props">
          <Row st={state.planningDate} t={t} label={t.planningDate} for="dlv-plan">
            <input id="dlv-plan" class="input input--quiet" type="date" value={s.planningDate ?? ''} onChange={(e) => void save('planningDate', (e.target as HTMLInputElement).value || null)} />
          </Row>
          <Row st={state.untilMonth} t={t} label={t.untilMonth} for="dlv-until">
            <input id="dlv-until" class="input input--quiet" type="month" value={s.untilMonth ?? ''} title={t.untilHint} onChange={(e) => void save('untilMonth', (e.target as HTMLInputElement).value || null)} />
          </Row>
        </dl>
      </MoreOptions>
    </section>
  );
}

/** One field: the label, the control, and how its save went. Outside the box so a field keeps focus across saves. */
function Row(q: { st?: State; t: (typeof TX)['en']; label: string; for?: string; children: ComponentChildren }) {
  const { st, t } = q;
  return (
    <div class="prop dlv-row">
      <dt>{q.for ? <label for={q.for}>{q.label}</label> : q.label}</dt>
      <dd>
        <div class="dlv-v">{q.children}</div>
        {st === 'saving' ? (
          <span class="dlv-st muted" aria-live="polite">
            {t.saving}
          </span>
        ) : st === 'saved' ? (
          <span class="dlv-st pill pill--ok" aria-live="polite">
            {t.saved} ✓
          </span>
        ) : st ? (
          <span class="dlv-st warn" role="alert">
            {t.failed}: {st.error}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

/** Subtitles: picking a language ends "No"; the last language off leaves it unset. */
function langToggle(cur: string[] | null, l: string): string[] | null {
  const now = cur ?? [];
  const next = now.includes(l) ? now.filter((x) => x !== l) : [...now, l];
  return next.length ? next : null;
}

/** A text that saves when you leave it or press Enter (only when it changed); Escape puts the old text back. */
function TextField(p: { id: string; value: string | null; placeholder: string; max: number; onSave: (v: string | null) => Promise<void> }) {
  const [x, setX] = useState(p.value ?? '');
  useEffect(() => setX(p.value ?? ''), [p.value]);
  const commit = () => {
    const next = x.trim() || null;
    if (next !== (p.value ?? null)) void p.onSave(next);
  };
  return (
    <input
      id={p.id}
      class="input input--quiet dlv-text"
      value={x}
      maxLength={p.max}
      placeholder={p.placeholder}
      onInput={(e) => setX((e.target as HTMLInputElement).value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setX(p.value ?? '');
      }}
    />
  );
}

/** "+ annat": a small field (or a list of the remaining choices) that adds one entry. */
function AddOther(p: { label: string; options?: [string, string][]; onAdd: (v: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [x, setX] = useState('');
  if (!open)
    return (
      <button type="button" class="chip dlv-add" onClick={() => setOpen(true)}>
        {p.label}
      </button>
    );
  const add = (v: string) => {
    const s = v.trim();
    setOpen(false);
    setX('');
    if (s) void p.onAdd(s);
  };
  return (
    <span class="dlv-other">
      <input
        class="input dlv-other-in"
        list={p.options?.length ? 'dlv-opts' : undefined}
        value={x}
        maxLength={40}
        aria-label={p.label.replace('+ ', '')}
        ref={(el) => el?.focus()}
        onInput={(e) => setX((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') add(p.options?.find(([, l]) => l.toLowerCase() === x.trim().toLowerCase())?.[0] ?? x);
          if (e.key === 'Escape') setOpen(false);
        }}
        onBlur={() => add(p.options?.find(([, l]) => l.toLowerCase() === x.trim().toLowerCase())?.[0] ?? x)}
      />
      {p.options?.length ? (
        <datalist id="dlv-opts">
          {p.options.map(([, l]) => (
            <option value={l} />
          ))}
        </datalist>
      ) : null}
    </span>
  );
}

/** "Annat": any whole number of working days. */
function DaysField(p: { label: string; value: number | null; onSave: (n: number | null) => Promise<void> }) {
  const [x, setX] = useState(p.value ? String(p.value) : '');
  useEffect(() => setX(p.value ? String(p.value) : ''), [p.value]);
  const commit = () => {
    const n = x.trim() ? Number(x) : null;
    if (n === p.value) return;
    if (n !== null && (!Number.isInteger(n) || n < 1 || n > MAX_DELIVERY_DAYS)) return setX(p.value ? String(p.value) : '');
    void p.onSave(n);
  };
  return (
    <label class={`chip dlv-days${p.value ? ' is-on' : ''}`} aria-pressed={p.value ? 'true' : 'false'}>
      {p.label}
      <input
        class="dlv-days-in"
        type="number"
        inputMode="numeric"
        min={1}
        max={MAX_DELIVERY_DAYS}
        value={x}
        placeholder="–"
        onInput={(e) => setX((e.target as HTMLInputElement).value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
    </label>
  );
}

/** The client's company the project is invoiced to: the main one preselected; another picked or added in Bokio (admin). */
function BillToField<V extends ProjectLike>(p: { api: Api; v: V; lang: Lang; onState: (s: State | null) => void; onSaved: (v: V) => void }) {
  const t = TX[p.lang];
  const b = p.v.billTo!;
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [orgNo, setOrgNo] = useState('');
  const pick = async (id: string | null) => {
    p.onState('saving');
    try {
      const r = await p.api.put<{ project: V }>(`/api/projects/${p.v.id}/bill-to`, { customerId: id });
      p.onSaved(r.project);
      p.onState('saved');
    } catch (e) {
      p.onState({ error: errText(e) });
    }
  };
  const add = async () => {
    p.onState('saving');
    try {
      const r = await p.api.post<{ companies: { id: string }[] }>('/api/finance/customers/company', { clientId: p.v.client.id, create: { name: name.trim(), orgNo: orgNo.trim() } });
      const fresh = r.companies.find((c) => !b.companies.some((x) => x.id === c.id));
      setAdding(false);
      setName('');
      setOrgNo('');
      await pick(fresh?.id ?? null);
    } catch (e) {
      p.onState({ error: errText(e) });
    }
  };
  if (adding)
    return (
      <form
        class="dlv-co"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <input class="input" placeholder={t.companyName} aria-label={t.companyName} value={name} maxLength={200} onInput={(e) => setName((e.target as HTMLInputElement).value)} required />
        <input class="input dlv-org" placeholder={t.orgNo} aria-label={t.orgNo} value={orgNo} maxLength={20} onInput={(e) => setOrgNo((e.target as HTMLInputElement).value)} />
        <button type="submit" class="btn btn-sm">
          {t.add}
        </button>
        <button type="button" class="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>
          {t.cancel}
        </button>
      </form>
    );
  return (
    <span class="dlv-billto">
      <select id="dlv-billto" class="input input--quiet" value={b.current ?? ''} onChange={(e) => void pick((e.target as HTMLSelectElement).value || null)}>
        <option value="">{t.mainCompany(b.main)}</option>
        {b.companies.map((c) => (
          <option value={c.id}>{c.name}</option>
        ))}
      </select>
      {b.canAdd ? (
        <button type="button" class="chip dlv-add" onClick={() => setAdding(true)}>
          {t.addCompany}
        </button>
      ) : null}
    </span>
  );
}
